import { parse } from 'acorn';
import { dateShapeOf, reshapeDate, showInstant, DATE_SHAPE_WORDS, type DateShape } from './dateShape';
import { humanize, readableDate } from './readable';

/**
 * Reading and rewriting the CONTENT of a generated web app.
 *
 * A web app keeps its content in `S.store(name, [ … ])` seed literals: the habits, the
 * menu items, the quiz questions. Those are the rows the owner most wants to change after
 * the app is built, and until now the only way was to ask for a whole regeneration, which
 * risks changing everything else too.
 *
 * These functions treat the source as the record of truth and edit it surgically — parse
 * with the same acorn the gate uses, take the exact character range of the array literal,
 * and splice a new one in. Nothing else in the file is touched, so an edit cannot disturb
 * the app's layout or logic.
 *
 * A collection is only editable when every value in it is a literal. `S.store('rows',
 * buildRows())` or a row holding a function call cannot be represented as data, and
 * pretending otherwise would silently drop whatever it actually computed.
 */
export interface SeedCollection {
  name: string;
  /** Where the rows live: a persisted collection, or a plain content array in the file. */
  kind: 'store' | 'const';
  /** Parsed rows, when the literal is plain data. */
  rows: Record<string, unknown>[];
  /** Field names across the rows, in first-seen order — the columns to edit. */
  fields: string[];
  /** False when the seed is not a plain literal, so it must not be rewritten. */
  editable: boolean;
  /** Character range of the array literal within the source. */
  start: number;
  end: number;
}

interface Node {
  type: string;
  start: number;
  end: number;
  [key: string]: unknown;
}

function walk(node: unknown, visit: (n: Node) => void): void {
  if (!node || typeof node !== 'object') return;
  if (Array.isArray(node)) { for (const c of node) walk(c, visit); return; }
  const n = node as Node;
  if (typeof n.type === 'string') visit(n);
  for (const key of Object.keys(n)) {
    if (key === 'type' || key === 'loc' || key === 'range') continue;
    walk(n[key], visit);
  }
}

/** A literal value, or `undefined` when the node is anything computed. */
function literalOf(node: Node | undefined): unknown {
  if (!node) return undefined;
  if (node.type === 'Literal') return (node as { value?: unknown }).value;
  if (node.type === 'UnaryExpression' && (node as { operator?: string }).operator === '-') {
    const arg = literalOf((node as { argument?: Node }).argument);
    return typeof arg === 'number' ? -arg : undefined;
  }
  if (node.type === 'ArrayExpression') {
    const out: unknown[] = [];
    for (const el of ((node as { elements?: Node[] }).elements ?? [])) {
      const v = literalOf(el);
      if (v === undefined && el?.type !== 'Literal') return undefined;
      out.push(v);
    }
    return out;
  }
  if (node.type === 'ObjectExpression') {
    const out: Record<string, unknown> = {};
    for (const prop of ((node as { properties?: Node[] }).properties ?? [])) {
      if (prop.type !== 'Property') return undefined;
      const key = prop.key as { name?: string; value?: unknown } | undefined;
      const name = key?.name ?? (typeof key?.value === 'string' ? key.value : undefined);
      if (!name) return undefined;
      const v = literalOf(prop.value as Node);
      if (v === undefined && (prop.value as Node)?.type !== 'Literal') return undefined;
      out[name] = v;
    }
    return out;
  }
  return undefined;
}

/** `const QUESTIONS = [ {...}, ... ]` — where most of an app's content actually lives.
 *  Surfacing only S.store seeds left the Content tab empty for apps whose menu items,
 *  questions or exercises sit in a plain array, which is most of them. */
function contentArray(n: Node): { name: string; array: Node } | null {
  if (n.type !== 'VariableDeclarator') return null;
  const id = n.id as { type?: string; name?: string } | undefined;
  const init = n.init as Node | undefined;
  if (id?.type !== 'Identifier' || !id.name || init?.type !== 'ArrayExpression') return null;
  const elements = (init.elements as Node[] | undefined) ?? [];
  // Only a list of plain records is content; an array of numbers or of calls is code.
  if (elements.length === 0 || !elements.every((e) => e?.type === 'ObjectExpression')) return null;
  return { name: id.name, array: init };
}

/** `var BANK = { 'Science': [ {...} ], 'History': [ … ] }` — a grouped content bank, which
 *  is how generated quizzes, menus and catalogues usually hold their records. Each group is
 *  offered separately, qualified by its variable so two banks cannot collide. */
function contentGroups(n: Node): { name: string; array: Node }[] {
  if (n.type !== 'VariableDeclarator') return [];
  const id = n.id as { type?: string; name?: string } | undefined;
  const init = n.init as Node | undefined;
  if (id?.type !== 'Identifier' || !id.name || init?.type !== 'ObjectExpression') return [];

  const out: { name: string; array: Node }[] = [];
  for (const prop of ((init.properties as Node[] | undefined) ?? [])) {
    if (prop.type !== 'Property') continue;
    const key = prop.key as { name?: string; value?: unknown } | undefined;
    const label = key?.name ?? (typeof key?.value === 'string' ? key.value : undefined);
    const value = prop.value as Node | undefined;
    if (!label || value?.type !== 'ArrayExpression') continue;
    const elements = (value.elements as Node[] | undefined) ?? [];
    if (elements.length === 0 || !elements.every((e) => e?.type === 'ObjectExpression')) continue;
    out.push({ name: `${id.name}.${label}`, array: value });
  }
  return out;
}

function isStoreCall(n: Node): boolean {
  if (n.type !== 'CallExpression') return false;
  const callee = n.callee as Node | undefined;
  if (!callee || callee.type !== 'MemberExpression') return false;
  const obj = callee.object as { type?: string; name?: string } | undefined;
  const prop = callee.property as { type?: string; name?: string } | undefined;
  return obj?.type === 'Identifier' && obj.name === 'S' && prop?.type === 'Identifier' && prop.name === 'store';
}

/** Every seeded collection in an app, with the range needed to rewrite it. */
export function readSeedCollections(source: string): SeedCollection[] {
  const out: SeedCollection[] = [];
  const re = /<script\b[^>]*>([\s\S]*?)<\/script>/gi;
  let match: RegExpExecArray | null;

  while ((match = re.exec(source)) !== null) {
    const body = match[1];
    const offset = match.index + match[0].indexOf(body);
    let program: Node;
    try {
      program = parse(body, { ecmaVersion: 2022 }) as unknown as Node;
    } catch {
      continue;
    }
    walk(program, (n) => {
      if (!isStoreCall(n)) return;
      const args = (n.arguments as Node[] | undefined) ?? [];
      const name = (args[0] as { value?: unknown } | undefined)?.value;
      const seed = args[1];
      if (typeof name !== 'string' || !seed || seed.type !== 'ArrayExpression') return;

      const value = literalOf(seed);
      const rows = Array.isArray(value) ? (value as Record<string, unknown>[]) : [];
      const editable = Array.isArray(value) && rows.every((r) => r && typeof r === 'object' && !Array.isArray(r));

      const fields: string[] = [];
      for (const row of rows) {
        if (!row || typeof row !== 'object') continue;
        for (const k of Object.keys(row)) if (k !== 'id' && !fields.includes(k)) fields.push(k);
      }

      out.push({ name, kind: 'store', rows, fields, editable, start: offset + seed.start, end: offset + seed.end });
    });

    const collect = (found: { name: string; array: Node }) => {
      const value = literalOf(found.array);
      const rows = Array.isArray(value) ? (value as Record<string, unknown>[]) : [];
      const editable = Array.isArray(value) && rows.every((r) => r && typeof r === 'object' && !Array.isArray(r));
      const fields: string[] = [];
      for (const row of rows) {
        if (!row || typeof row !== 'object') continue;
        for (const k of Object.keys(row)) if (k !== 'id' && !fields.includes(k)) fields.push(k);
      }
      // A store seed already covers the same range; don't offer the same rows twice.
      if (out.some((c) => c.start === offset + found.array.start)) return;
      out.push({
        name: found.name, kind: 'const', rows, fields, editable,
        start: offset + found.array.start, end: offset + found.array.end,
      });
    };

    walk(program, (n) => {
      const single = contentArray(n);
      if (single) collect(single);
      for (const group of contentGroups(n)) collect(group);
    });
  }
  return out;
}

/**
 * Replace one collection's seed rows, leaving every other byte of the app alone.
 *
 * Ranges are recomputed on the source passed in rather than trusted from an earlier read,
 * because any edit shifts every offset after it — reusing a stale range would splice into
 * the middle of the file.
 */
export function writeSeedCollection(
  source: string,
  name: string,
  rows: Record<string, unknown>[],
): string {
  const target = readSeedCollections(source).find((c) => c.name === name);
  if (!target) throw new Error(`This app has no "${name}" content to edit.`);
  if (!target.editable) throw new Error(`"${name}" is built in code, so it cannot be edited as a list.`);

  const literal = JSON.stringify(rows, null, 2)
    .split('\n')
    .map((line, i) => (i === 0 ? line : `  ${line}`))
    .join('\n');

  return source.slice(0, target.start) + literal + source.slice(target.end);
}

/**
 * Showing a field for editing, and putting it back in the shape it had.
 *
 * Kept here rather than in the panel so the round trip is testable: a field like
 * `options: ["Mars", "Venus"]` shown as `String(value)` gives "Mars,Venus", and saving
 * that back as a plain string turns a multiple-choice question into one long answer —
 * silently breaking the app it was meant to fix.
 */
export function displayValue(value: unknown): string {
  if (value === undefined || value === null) return '';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  // A stored instant is shown in the owner's own day and clock; `coerceValue` turns what they
  // type back into an instant, so the column keeps holding instants. See dateShape.ts.
  if (typeof value === 'string' && dateShapeOf(value) === 'instant') return showInstant(value);
  return String(value);
}

/**
 * Showing a field for READING: a row's title and second line in a list, a reply.
 *
 * Those lines used `String(value)` on the phone, so a shift read "2026-10-26T14:00:00.000Z" and a
 * seeded one "-3d 10:00". A date is read as a date (`readableDate`, "Mon, Oct 26, 2:00 PM"), a
 * token the page cannot date is said to be one, and anything else is shown as the editor shows
 * it. Never put this in an input: the editor's text is `displayValue`'s, because that is the text
 * `coerceValue` turns back into what was stored.
 */
export function readableValue(value: unknown, locale?: string): string {
  return readableDate(value, { locale }) ?? displayValue(value);
}

/** A shape a field holds and an edit must keep: a date or time, or a yes-or-no. */
export type ValueShape = DateShape | 'boolean';

const SHAPE_WORDS: Record<ValueShape, { what: string; example: string }> = {
  ...DATE_SHAPE_WORDS,
  boolean: { what: 'a yes or a no', example: 'yes or no' },
};

/**
 * The owner typed something that is not the date, time or yes-or-no this field holds.
 *
 * Refused rather than stored, because stored is worse: the app reads the column in the shape
 * it chose, so "next Tuesday" in a date column is a shift that renders as a blank card with
 * nothing anywhere saying why. The message names the form that works, and names the field as
 * its label does — "Starts at", not `startsAt` — since that is the word the owner is looking at.
 * `field` itself stays the stored name.
 */
export class ValueShapeError extends Error {
  readonly shape: ValueShape;
  readonly field?: string;
  constructor(shape: ValueShape, field?: string) {
    const words = SHAPE_WORDS[shape];
    super(`${field ? `"${humanize(field)}"` : 'This'} holds ${words.what} — write it like ${words.example}.`);
    this.name = 'ValueShapeError';
    this.shape = shape;
    this.field = field;
  }
}

/**
 * The words people type for a yes-or-no, or null for anything else.
 *
 * Only `true` used to count as yes, so "Yes" in a done/published/confirmed field was stored
 * as false — the owner said yes and the app heard no, with nothing on screen to say so. An
 * empty field is not in either list: emptying a `published: true` is not the same as typing
 * "no", and guessing that it is switches something off unasked.
 */
const YES_WORDS = new Set(['true', 'yes', 'y', 'on', '1']);
const NO_WORDS = new Set(['false', 'no', 'n', 'off', '0']);

function readYesNo(text: string): boolean | null {
  const word = text.trim().toLowerCase();
  if (YES_WORDS.has(word)) return true;
  if (NO_WORDS.has(word)) return false;
  return null;
}

/**
 * What the owner typed, in the shape the field already has.
 *
 * `previous` is the value being edited. `example` stands for the rest of the column, and is
 * only consulted when there is no previous value — a new row, or one added blank — so that
 * the new row arrives in the same shape as the rows around it, not as whatever string was
 * typed. A date, time or yes-or-no shape is ENFORCED (what does not fit is refused) unless
 * `firm` is false, which says the column only suggests the shape: what fits is reshaped and
 * what does not is kept as typed. `applyDraft` decides which from every row in the column
 * (see `neighbourExample`). The value being edited is always firm: it is this row's own.
 *
 * Text that is exactly what `displayValue` showed comes back as `previous` itself: saving a
 * row the owner did not touch must not change it. That matters most for an instant, which is
 * shown in local time to the minute and would otherwise lose its seconds on every save.
 */
export function coerceValue(previous: unknown, text: string, example?: unknown, firm = true): unknown {
  if (previous !== undefined && text === displayValue(previous)) return previous;

  const blank = previous === undefined || previous === null || previous === '';
  const typed = text.trim() !== '';
  const like = blank && typed && example !== undefined && example !== null && example !== '' ? example : previous;
  const fromNeighbour = like !== previous;
  // Only a shape borrowed from the column can be a suggestion; this row's own never is.
  const enforce = !fromNeighbour || firm;

  if (typeof like === 'number') { const n = Number(text); return Number.isFinite(n) ? n : text; }
  if (typeof like === 'boolean') {
    const yes = readYesNo(text);
    if (yes === null) {
      if (!enforce) return text;
      throw new ValueShapeError('boolean');
    }
    return yes;
  }
  if (Array.isArray(like)) {
    const parts = text.split(',').map((t) => t.trim()).filter((t) => t.length > 0);
    const numeric = parts.length > 0 && parts.every((t) => Number.isFinite(Number(t)));
    return numeric && like.every((v) => typeof v === 'number') ? parts.map(Number) : parts;
  }
  if (like && typeof like === 'object') {
    // A neighbour's object is only a hint about the shape; it must never be copied in.
    try { return JSON.parse(text); } catch { return fromNeighbour ? text : previous; }
  }
  if (typeof like === 'string') {
    const shape = dateShapeOf(like);
    if (shape) {
      // Emptied on purpose: the field is cleared, not turned into a string of spaces.
      if (!typed) return '';
      // Only the value being replaced may supply a part the owner did not retype.
      const out = reshapeDate(text, like, fromNeighbour ? undefined : like);
      if (out === null) {
        if (!enforce) return text;
        throw new ValueShapeError(shape);
      }
      return out;
    }
  }
  return text;
}

/**
 * The value a new or blank field takes its shape from, given the same field in the other rows,
 * and whether that shape is FIRM (what does not fit is refused) or only a suggestion (what
 * fits is reshaped, anything else is kept as typed). No example means the text is stored as
 * typed.
 *
 * Refusing needs the column's word, not one row's; reshaping needs much less, because it only
 * ever turns what the owner typed into the same thing written the column's way:
 *
 *  - A date or time shape is firm when at least two rows hold it and no filled row holds
 *    anything else. A string that parses as a clock can be a coincidence — a notes column whose
 *    only row reads '18:00' — and enforcing it refused "18:00 – bring apron" as not a time.
 *  - With a single row it is a suggestion. That keeps a small list working — the second shift
 *    typed as '6pm' still arrives as '18:00', which a column of one used to lose — while the
 *    note beside it is kept as written. Only text that reads as a date or time is reshaped
 *    (`reshapeDate` reads whole values, never a time inside a sentence).
 *  - Rows that disagree (two shapes, or a shape and plain words) settle nothing.
 *  - A yes-or-no is firm when every filled row is one. A JS boolean is never a coincidence of
 *    typing, so one row is enough. In a column that also holds 'pending' it is a suggestion:
 *    "yes" still arrives as true, and "maybe" is kept, because refusing it there overrules
 *    what the app itself wrote.
 *
 * A number, list or object still follows the first filled row, as before: none of those is
 * ever refused, so a wrong guess costs nothing the owner cannot see and retype.
 *
 * The value being edited is not decided here: when it already has a shape, `coerceValue`
 * keeps it, whatever its neighbours hold — editing an existing date keeps it a date.
 */
function neighbourExample(values: unknown[]): { example?: unknown; firm: boolean } {
  const filled = values.filter(
    (v) => v !== undefined && v !== null && !(typeof v === 'string' && v.trim() === ''),
  );
  const first = filled[0];
  if (typeof first === 'string') {
    const shape = dateShapeOf(first);
    if (!shape) return { example: first, firm: true };
    if (!filled.every((v) => dateShapeOf(v) === shape)) return { firm: false };
    return { example: first, firm: filled.length >= 2 };
  }
  if (typeof first === 'boolean') return { example: first, firm: filled.every((v) => typeof v === 'boolean') };
  return { example: first, firm: true };
}

/**
 * A row as the owner edited it, every field in the shape its column already holds.
 *
 * Both editors — Content (rows written into the page) and Live (rows held on the server) —
 * build what they save through this one function, so the rule cannot be right in one and
 * missing in the other. `siblings` are the other rows in the same list: what the app
 * already wrote is the truest statement of the shape it reads.
 */
export function applyDraft(
  original: Record<string, unknown> | undefined,
  draft: Record<string, string>,
  siblings: Record<string, unknown>[] = [],
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [field, text] of Object.entries(draft)) {
    const { example, firm } = neighbourExample(siblings.map((r) => r?.[field]));
    try {
      out[field] = coerceValue(original?.[field], text, example, firm);
    } catch (e) {
      if (e instanceof ValueShapeError) throw new ValueShapeError(e.shape, field);
      throw e;
    }
  }
  return out;
}

/**
 * A blank row shaped like the rows already there.
 *
 * Filling every field with '' looked harmless and was not: a numeric field became the
 * string "", so the moment the app did arithmetic on it the screen showed NaN. A new row
 * has to arrive in the same shape as its neighbours.
 */
export function blankRow(rows: Record<string, unknown>[], fields: string[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const field of fields) {
    const sample = rows.find((r) => r?.[field] !== undefined && r?.[field] !== null)?.[field];
    if (typeof sample === 'number') out[field] = 0;
    else if (typeof sample === 'boolean') out[field] = false;
    else if (Array.isArray(sample)) out[field] = [];
    else if (sample && typeof sample === 'object') out[field] = {};
    else out[field] = '';
  }
  return out;
}

/**
 * The words on the page that are not in a list: headings, button labels, the intro
 * paragraph, the empty-state line.
 *
 * These are the edits an owner asks for most ("change the button to say Reserve") and the
 * only route to them was regenerating the whole app from chat, which risks changing
 * everything else to fix four words.
 *
 * Only static markup is offered. Text built inside `<script>` is computed — it may be a
 * template, a number, a name from a row — and presenting it as a fixed string would let
 * someone "fix" a label that is rewritten a moment later, or break the code around it.
 */
export interface CopyItem {
  /** What a reader sees now. */
  text: string;
  /** Where it sits, so a list of loose phrases is identifiable: `h1`, `button`, `p`. */
  context: string;
  /** Character range of the editable text within the source. */
  start: number;
  end: number;
}

/**
 * `context` in words an owner uses. The tag is how the extractor tells two identical phrases
 * apart; shown raw it read "div", "span", "h2" under every line of the owner's own page.
 */
export function copyContextLabel(context: string): string {
  const tag = context.toLowerCase();
  if (/^h[1-6]$/.test(tag)) return 'Heading';
  if (tag === 'button') return 'Button';
  if (tag === 'a') return 'Link';
  if (tag === 'label') return 'Label';
  if (tag === 'li') return 'List item';
  if (tag === 'th' || tag === 'td') return 'Table';
  if (tag === 'option') return 'Choice';
  return 'Text';
}

/** Spans the extractor must not walk into: their contents are code, not copy. */
const OPAQUE = /<script\b[\s\S]*?<\/script>|<style\b[\s\S]*?<\/style>|<!--[\s\S]*?-->/gi;

function opaqueRanges(source: string): [number, number][] {
  const out: [number, number][] = [];
  OPAQUE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = OPAQUE.exec(source)) !== null) out.push([m.index, m.index + m[0].length]);
  return out;
}

/** Copy is text a person reads. A lone number, arrow or bullet is a placeholder the app
 *  overwrites at runtime, and offering "3:00" for editing is noise at best. */
function isReadable(text: string): boolean {
  return /\p{L}/u.test(text);
}

/**
 * Markup in, words out — and back again on save.
 *
 * Without this pair, copy already containing an entity round-trips wrong: the editor shows
 * a literal "Fish &amp; chips", and saving it escapes the ampersand a second time, so the
 * page ends up reading "Fish &amp;amp; chips". Whoever is editing sees words, and words
 * are what comes back.
 */
const ENTITIES: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', '#39': "'", apos: "'", nbsp: ' ' };

function decodeEntities(text: string): string {
  return text.replace(/&(#?[a-z0-9]+);/gi, (whole, name: string) => ENTITIES[name.toLowerCase()] ?? whole);
}

export function escapeCopy(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export function readCopy(source: string): CopyItem[] {
  const skip = opaqueRanges(source);
  const inSkip = (i: number) => skip.some(([a, b]) => i >= a && i < b);
  const out: CopyItem[] = [];

  // Text nodes: whatever sits between a closing `>` and the next `<`.
  const node = />([^<>]+)</g;
  let m: RegExpExecArray | null;
  while ((m = node.exec(source)) !== null) {
    if (inSkip(m.index)) continue;
    const raw = m[1];
    const text = raw.trim();
    if (!text || !isReadable(text)) continue;
    const start = m.index + 1 + raw.indexOf(text);
    // The tag this text sits inside, read backwards from the match — the only clue that
    // separates a heading from a button in a flat list of phrases.
    const open = source.lastIndexOf('<', m.index);
    const context = /^<\s*([a-zA-Z][a-zA-Z0-9]*)/.exec(source.slice(open, m.index + 1))?.[1]?.toLowerCase() ?? 'text';
    out.push({ text: decodeEntities(text), context, start, end: start + text.length });
  }

  // Placeholders are copy too, and the only words in an empty form.
  const placeholder = /\splaceholder\s*=\s*"([^"]*)"/gi;
  while ((m = placeholder.exec(source)) !== null) {
    if (inSkip(m.index)) continue;
    const text = m[1].trim();
    if (!text || !isReadable(text)) continue;
    const start = m.index + m[0].indexOf(m[1]);
    out.push({ text: decodeEntities(text), context: 'placeholder', start, end: start + m[1].length });
  }

  return out.sort((a, b) => a.start - b.start);
}

/**
 * Replace one piece of copy, leaving every other byte alone.
 *
 * Addressed by position in a freshly-read list rather than by a remembered range: any
 * edit shifts every offset after it, and splicing at a stale range would cut the file
 * somewhere arbitrary. If the source no longer has that many pieces the edit is refused
 * instead of landing on whichever phrase happens to sit at that index now.
 */
export function writeCopy(source: string, index: number, text: string): string {
  const items = readCopy(source);
  const target = items[index];
  if (!target) throw new Error('That text is no longer in this app.');

  const safe = text.trim();
  if (!safe) throw new Error('Give this some words, or the app shows an empty space.');
  // `<` would open a tag and `&` could start an entity: either turns typed words into
  // markup, which at best breaks the layout the app was built with.
  return source.slice(0, target.start) + escapeCopy(safe) + source.slice(target.end);
}

/**
 * Structure changes to a collection: a new column, a column removed, rows reordered.
 *
 * All three are row rewrites, so they go back through `writeSeedCollection` and inherit
 * its guarantee that nothing outside the array literal is touched.
 */
export function addField(rows: Record<string, unknown>[], field: string): Record<string, unknown>[] {
  const name = field.trim();
  if (!name) throw new Error('Give the field a name.');
  if (rows.some((r) => name in r)) throw new Error(`There is already a "${name}".`);
  return rows.map((r) => ({ ...r, [name]: '' }));
}

export function removeField(rows: Record<string, unknown>[], field: string): Record<string, unknown>[] {
  return rows.map((r) => {
    const next = { ...r };
    delete next[field];
    return next;
  });
}

/** Move one row by `delta` places, clamped — a row at the top cannot go higher. */
export function moveRow(rows: Record<string, unknown>[], index: number, delta: number): Record<string, unknown>[] {
  const to = index + delta;
  if (index < 0 || index >= rows.length || to < 0 || to >= rows.length) return rows;
  const next = rows.slice();
  const [row] = next.splice(index, 1);
  next.splice(to, 0, row);
  return next;
}
